import React, { useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { MAINTENANCE_PLANS, MAINTENANCE_COMPARISON, MAINTENANCE_BENEFITS, MAINTENANCE_TESTIMONIAL, PRICING } from "../mock/mock";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Zap, Clock, FileText, CheckCircle2, X, MessageCircle, Server, ArrowRight, Phone, Mail, Globe, Shield } from "lucide-react";
import { WHATSAPP_LINK } from "../config/contact";
import { submitLead } from "../lib/lead";
import SEO from "../components/SEO";
import Header from "../components/Header";
const urlPattern = /^(https?:\/\/)?(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/[^\s]*)?$/i;

const schema = z.object({
  nombre: z.string().min(2, "Ingresa tu nombre"),
  email: z.string().email("Correo inválido"),
  telefono: z.string().min(7, "Teléfono inválido"),
  url: z.string().trim().optional().refine((value) => !value || urlPattern.test(value), "URL inválida"),
  mensaje: z.string().min(5, "Cuéntanos brevemente tu caso"),
  plan: z.string(),
  urgencia: z.string(),
  canal: z.string(),
  consentimiento: z.boolean().refine(v => v, { message: "Requerimos tu consentimiento" })
});

const BenefitIcon = ({ name }) => {
  const map = { Shield, Zap, Clock, FileText };
  const Icon = map[name] || Shield;
  return <Icon className="w-8 h-8 text-secondary" />;
};

export default function Mantenimiento() {
  const { toast } = useToast();
  const formRef = useRef(null);
  
  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre: "",
      email: "",
      telefono: "",
      url: "",
      mensaje: "",
      plan: "mantenimiento",
      urgencia: "consulta",
      canal: "Email",
      consentimiento: false
    }
  });
  const consentimiento = watch("consentimiento");
  const openWhatsApp = () => window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer");

  const onSubmit = async (data) => {
    const result = await submitLead(data, "mantenimiento-page");
    if (result.delivered) {
      toast({ 
        title: "¡Solicitud enviada!", 
        description: "Te contactaremos en breve por tu canal preferido.", 
      });
      formRef.current?.reset?.();
    } else {
      toast({ 
        title: "No pudimos enviar tu solicitud", 
        description: "Por favor contáctanos por WhatsApp para atención inmediata.", 
      });
    }
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <>
      <SEO 
        title="Mantenimiento WordPress Proactivo | Blindaje 24/7"
        description="Servicio de mantenimiento WordPress profesional de GAREY AW. Actualizaciones, seguridad, backups y optimización. Planes desde $99 USD/mes. Blindaje total 24/7."
        keywords="mantenimiento wordpress, actualización wordpress, seguridad wordpress, backups wordpress, monitoreo wordpress, wordpress administrado"
        canonicalUrl="https://wprescate24.com/mantenimiento-wordpress"
      />
      <div className="dark min-h-screen bg-background">
      <Header 
        currentPage="mantenimiento"
        internalLinks={[
          { href: "#planes", label: "Planes" },
          { href: "#beneficios", label: "Beneficios" },
          { href: "#comparacion", label: "Comparación" }
        ]}
        ctaText="Ver Planes"
        ctaHref="#planes"
        ctaColor="green"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-secondary/5 via-background to-primary/5">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/30 mb-6">
            <Shield className="w-4 h-4 text-secondary" />
            <span className="text-sm font-medium text-secondary">Blindaje Proactivo WordPress</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight max-w-4xl mx-auto">
            Tu Sitio Seguro y Actualizado, <span className="text-secondary">Siempre</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-foreground/80 max-w-3xl mx-auto leading-relaxed">
            <strong>Nuestra agencia especializada</strong> se encarga de las actualizaciones, seguridad, backups y velocidad. Tú enfócate en tu negocio.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <Button className="bg-secondary hover:bg-secondary/90 text-white font-bold px-8 py-6 text-base" asChild>
              <a href="#planes">🛡️ Ver Planes de Blindaje</a>
            </Button>
            <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary/5 font-semibold px-8 py-6 text-base" onClick={openWhatsApp}>
              Hablar por WhatsApp
            </Button>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-8 text-sm text-foreground/70">
            <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-secondary" /> <strong>Monitoreo 24/7</strong></div>
            <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-secondary" /> <strong>Backups Automáticos</strong></div>
            <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-secondary" /> <strong>Actualizaciones Seguras</strong></div>
          </div>
        </div>
      </section>
      {/* Quick Contact Strip */}
      <section className="py-5 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm font-semibold">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>Respuesta inicial en minutos</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Diagnóstico técnico sin costo</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span>Sin compromiso</span>
            </div>
          </div>
          <Button className="bg-white text-secondary hover:bg-white/90 font-bold" onClick={openWhatsApp}>
            <MessageCircle className="w-4 h-4 mr-2" />
            Quiero atención por WhatsApp
          </Button>
        </div>
      </section>

      {/* Beneficios */}
      <section id="beneficios" className="py-20 bg-background border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">¿Por qué mantener tu sitio con nosotros?</h2>
            <p className="mt-4 text-lg text-foreground/70">Más que mantenimiento: un blindaje proactivo integral</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {MAINTENANCE_BENEFITS.map((benefit) => (
              <Card key={benefit.title} className="bg-gradient-to-br from-secondary/5 to-primary/5 border-2 border-border hover:border-secondary/50 transition-all hover:shadow-xl">
                <CardHeader>
                  <div className="h-16 w-16 rounded-2xl bg-secondary/10 flex items-center justify-center mb-4">
                    <BenefitIcon name={benefit.icon} />
                  </div>
                  <CardTitle className="text-foreground text-lg font-bold">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-foreground/70 text-sm leading-relaxed">
                  {benefit.desc}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparación */}
      <section id="comparacion" className="py-20 bg-gradient-to-b from-background to-secondary/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Gestionarlo tú vs. Nuestro Blindaje Proactivo</h2>
            <p className="mt-4 text-lg text-foreground/70">La diferencia entre estrés constante y total tranquilidad</p>
          </div>
          <div className="space-y-4">
            {MAINTENANCE_COMPARISON.map((item, idx) => (
              <Card key={idx} className="bg-background border-2 border-border overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
                    <div className="p-6 flex items-center">
                      <p className="font-bold text-foreground">{item.problem}</p>
                    </div>
                    <div className="p-6 bg-destructive/5 flex items-start gap-3">
                      <X className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-foreground/80">{item.withoutUs}</p>
                    </div>
                    <div className="p-6 bg-secondary/5 flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-foreground/80 font-medium">{item.withUs}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Planes */}
      <section id="planes" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Planes de Blindaje WordPress</h2>
            <p className="text-lg text-foreground/70 mt-4">Elige el nivel de protección adecuado para tu sitio</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {MAINTENANCE_PLANS.map((plan) => (
              <Card key={plan.id} className={`relative bg-background border-2 ${plan.highlight ? "border-secondary shadow-xl shadow-secondary/10 scale-105" : "border-border"} overflow-hidden transition-all hover:shadow-2xl`}>
                {plan.highlight && (
                  <div className="absolute right-4 top-4 text-xs px-3 py-1 rounded-full bg-secondary text-white font-bold">Más Popular</div>
                )}
                <CardHeader className="space-y-4">
                  <CardTitle className="text-foreground">
                    <div className="text-2xl font-bold">{plan.name}</div>
                    <div className="flex items-baseline gap-2 mt-3">
                      <span className="text-4xl font-bold text-secondary">${plan.price}</span>
                      <span className="text-foreground/60">/{plan.period}</span>
                    </div>
                  </CardTitle>
                  <p className="text-sm text-foreground/70">{plan.description}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm text-foreground/80">
                        <CheckCircle2 className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className={`w-full font-bold ${plan.highlight ? 'bg-secondary hover:bg-secondary/90' : 'bg-primary hover:bg-primary/90'} text-white`} asChild>
                    <a href={WHATSAPP_LINK} target="_blank" rel="noopener">
                      Contratar {plan.name}
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonio */}
      <section className="py-20 bg-gradient-to-br from-secondary/10 via-background to-primary/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Historia de Éxito</h2>
            <p className="text-lg text-foreground/70 mt-4">{MAINTENANCE_TESTIMONIAL.highlight}</p>
          </div>
          <Card className="bg-background border-2 border-secondary/30 shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <blockquote className="text-lg text-foreground/90 italic leading-relaxed mb-8">
                "{MAINTENANCE_TESTIMONIAL.quote}"
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-secondary/10 border-2 border-secondary/30 text-secondary flex items-center justify-center text-xl font-bold">
                  {MAINTENANCE_TESTIMONIAL.initials}
                </div>
                <div>
                  <div className="text-foreground text-lg font-bold">{MAINTENANCE_TESTIMONIAL.name}</div>
                  <div className="text-foreground/60">{MAINTENANCE_TESTIMONIAL.role}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Hosting */}
      <section className="py-16 bg-gradient-to-r from-primary/10 to-secondary/10 border-y border-border">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Server className="w-16 h-16 text-primary mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            ¿Quieres rendimiento máximo?
          </h2>
          <p className="text-lg text-foreground/70 mb-8 max-w-2xl mx-auto">
            Combina nuestro blindaje proactivo con <strong>hosting especializado WordPress</strong> para velocidad y seguridad sin igual.
          </p>
          <p className="text-sm text-foreground/60 mb-6">(Próximamente disponible - mientras tanto, contáctanos para una cotización)</p>
          <Button className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-6 text-base" asChild>
            <a href="#contacto">
              Cotizar Hosting + Mantenimiento <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
        </div>
      </section>

      {/* Formulario de Contacto */}
      <section id="contacto" className="py-20 bg-gradient-to-b from-background to-secondary/5 border-t border-border">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">¿Listo para dormir tranquilo?</h2>
            <p className="text-lg text-foreground/70 mt-4">Cuéntanos sobre tu sitio y te contactaremos para diseñar tu plan de blindaje</p>
          </div>
          <div ref={formRef} className="rounded-xl bg-gradient-to-br from-secondary/5 to-primary/5 border-2 border-border p-8">
            <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="col-span-1">
                <Label htmlFor="nombre" className="text-foreground font-medium">Nombre</Label>
                <Input id="nombre" placeholder="Tu nombre" {...register("nombre")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.nombre && <p className="text-destructive text-xs mt-1">{errors.nombre.message}</p>}
              </div>
              <div className="col-span-1">
                <Label htmlFor="email" className="text-foreground font-medium">Email</Label>
                <Input id="email" placeholder="tucorreo@ejemplo.com" {...register("email")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
              </div>
              <div className="col-span-1">
                <Label htmlFor="telefono" className="text-foreground font-medium">Teléfono</Label>
                <Input id="telefono" placeholder="+52 722 381 8617" {...register("telefono")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.telefono && <p className="text-destructive text-xs mt-1">{errors.telefono.message}</p>}
              </div>
              <div className="col-span-1">
                <Label htmlFor="url" className="text-foreground font-medium">URL del sitio (opcional)</Label>
                <Input id="url" placeholder="tusitio.com (opcional)" {...register("url")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.url && <p className="text-destructive text-xs mt-1">{errors.url.message}</p>}
              </div>
              <div className="col-span-1">
                <Label className="text-foreground font-medium">Servicio de interés</Label>
                <Select onValueChange={(v) => setValue("plan", v)} defaultValue="mantenimiento">
                  <SelectTrigger className="bg-background border-border text-foreground">
                    <SelectValue placeholder="Elige un servicio" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border-border text-foreground">
                    <SelectItem value="rescate">Rescate</SelectItem>
                    <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                    <SelectItem value="optimizacion">Optimización</SelectItem>
                    <SelectItem value="hosting">Hosting Profesional</SelectItem>
                  </SelectContent>
                </Select>
                {errors.plan && <p className="text-destructive text-xs mt-1">{errors.plan.message}</p>}
              </div>
              <div className="col-span-1">
                <Label className="text-foreground font-medium">Canal preferido</Label>
                <Select onValueChange={(v) => setValue("canal", v)} defaultValue="Email">
                  <SelectTrigger className="bg-background border-border text-foreground">
                    <SelectValue placeholder="¿Cómo te contactamos?" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border-border text-foreground">
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                    <SelectItem value="Llamada">Llamada</SelectItem>
                  </SelectContent>
                </Select>
                {errors.canal && <p className="text-destructive text-xs mt-1">{errors.canal.message}</p>}
              </div>
              <div className="md:col-span-2 col-span-1">
                <Label htmlFor="mensaje" className="text-foreground font-medium">Cuéntanos sobre tu sitio</Label>
                <Textarea id="mensaje" rows={4} placeholder="Ej: Tengo un ecommerce en WooCommerce con 5000 visitas mensuales..." {...register("mensaje")} className="bg-background border-border text-foreground placeholder:text-foreground/40" />
                {errors.mensaje && <p className="text-destructive text-xs mt-1">{errors.mensaje.message}</p>}
              </div>
              <div className="md:col-span-2 col-span-1 flex items-center gap-2">
                <Checkbox
                  id="consentimiento"
                  checked={consentimiento}
                  onCheckedChange={(checked) => setValue("consentimiento", checked)}
                />
                <Label htmlFor="consentimiento" className="text-sm text-foreground/70">Acepto ser contactado y la política de privacidad.</Label>
              </div>
              {errors.consentimiento && <p className="md:col-span-2 col-span-1 text-destructive text-xs -mt-2">{errors.consentimiento.message}</p>}
              <div className="md:col-span-2 col-span-1">
                <Button type="submit" disabled={isSubmitting} className="w-full bg-secondary hover:bg-secondary/90 text-white font-bold py-6">
                  {isSubmitting ? "Enviando..." : "Solicitar Información"}
                </Button>
                <p className="mt-3 text-xs text-foreground/70 text-center">Normalmente respondemos en menos de 15 minutos por WhatsApp o correo.</p>
              </div>
            </form>
            <div className="mt-4 text-xs text-foreground/60 flex items-center gap-4">
              <div className="flex items-center gap-1"><Phone className="w-4 h-4" /> Soporte 24/7</div>
              <div className="flex items-center gap-1"><Mail className="w-4 h-4" /> Respuesta rápida</div>
              <div className="flex items-center gap-1"><Globe className="w-4 h-4" /> Sin compromiso</div>
            </div>
          </div>
          <div className="text-center mt-8">
            <p className="text-foreground/60 mb-4">¿Prefieres hablar directamente?</p>
            <Button className="bg-secondary hover:bg-secondary/90 text-white font-bold px-8 py-4" asChild>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener">
                <MessageCircle className="w-5 h-5 mr-2" />
                Contactar por WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-[#b8cd37]" />
                <span className="font-bold text-white text-lg">WPRescate24</span>
              </div>
              <p className="text-sm text-white/70">
                Agencia especializada en: optimización, rescate, seguridad y blindaje de WordPress y sitios web.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-3">Servicios</h4>
              <div className="flex flex-col gap-2">
                <RouterLink to="/" className="text-sm text-white/70 hover:text-white transition-colors">Rescate WordPress</RouterLink>
                <RouterLink to="/mantenimiento-wordpress" className="text-sm text-white/70 hover:text-white transition-colors">Mantenimiento Proactivo</RouterLink>
                <RouterLink to="/hosting-wordpress" className="text-sm text-white/70 hover:text-white transition-colors">Hosting WordPress</RouterLink>
                <RouterLink to="/blog" className="text-sm text-white/70 hover:text-white transition-colors">Blog</RouterLink>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-white mb-3">Infraestructura</h4>
              <p className="text-sm text-white/70 mb-2">Nuestros servicios están respaldados por:</p>
              <a 
                href="https://servidores.garey.mx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#b8cd37] hover:text-[#c6da45] transition-colors"
              >
                <Server className="w-4 h-4" />
                servidores.garey.mx
              </a>
            </div>
          </div>
          <div className="border-t border-white/15 pt-6 flex flex-col md:flex-row items-center justify-between text-sm text-white/60">
            <div>© {new Date().getFullYear()} WPRescate24. Todos los derechos reservados. Una empresa de <a href="https://garey.mx" target="_blank" rel="noopener noreferrer" className="text-white hover:underline font-semibold">GAREY AW</a></div>
            <div className="flex items-center gap-6 mt-3 md:mt-0">
              <RouterLink to="/terminos" className="hover:text-white transition-colors">Términos</RouterLink>
              <RouterLink to="/privacidad" className="hover:text-white transition-colors">Privacidad</RouterLink>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener" className="hover:text-[#b8cd37] transition-colors">WhatsApp</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a href={WHATSAPP_LINK} target="_blank" rel="noopener" aria-label="Abrir WhatsApp" className="fixed bottom-6 right-6 z-50 group">
        <div className="h-16 w-16 rounded-full bg-[#25D366] hover:bg-[#20bd5a] flex items-center justify-center shadow-2xl border-2 border-white/30 transition-all hover:scale-110">
          <MessageCircle className="w-8 h-8 text-white" />
        </div>
      </a>
      </div>
    </>
  );
}
